import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelineSectionProps {
  className?: string;
}

interface MilestoneItem {
  id: number;
  year: string;
  quarter: string;
  title: string;
  description: string;
  icon: string;
}

const TimelineSection = ({ className = '' }: TimelineSectionProps) => {
  const milestones: MilestoneItem[] = [
    {
      id: 1,
      year: '2022',
      quarter: 'Q1',
      title: 'Foundation',
      description: 'AI Change Leaders founded by three senior transformation consultants who identified the critical gap between AI investment and organizational adoption.',
      icon: 'RocketLaunchIcon',
    },
    {
      id: 2,
      year: '2022',
      quarter: 'Q3',
      title: 'First Enterprise Client',
      description: 'Delivered first major AI transformation engagement for UK financial services firm, establishing proven methodology framework.',
      icon: 'BuildingOfficeIcon',
    },
    {
      id: 3,
      year: '2023',
      quarter: 'Q2',
      title: 'Partner Network Launch',
      description: 'Launched curated partner network model, bringing together 15 senior consultants specializing in AI change leadership.',
      icon: 'UserGroupIcon',
    },
    {
      id: 4,
      year: '2023',
      quarter: 'Q4',
      title: 'Industry Recognition',
      description: 'Featured in Financial Times as emerging leader in AI transformation consulting. Speaking engagements at major industry conferences.',
      icon: 'TrophyIcon',
    },
    {
      id: 5,
      year: '2024',
      quarter: 'Q2',
      title: 'Methodology Framework',
      description: 'Published comprehensive AI Change Leadership Framework, adopted by multiple Fortune 500 companies for transformation initiatives.',
      icon: 'BookOpenIcon',
    },
    {
      id: 6,
      year: '2024',
      quarter: 'Q4',
      title: 'Global Expansion',
      description: 'Expanded operations to serve clients across Europe and North America. Partner network grew to 40+ senior consultants.',
      icon: 'GlobeAltIcon',
    },
    {
      id: 7,
      year: '2025',
      quarter: 'Q3',
      title: 'Thought Leadership',
      description: 'Launched AI Transformation Insights series, providing research and frameworks to support enterprise AI adoption globally.',
      icon: 'LightBulbIcon',
    },
    {
      id: 8,
      year: '2026',
      quarter: 'Q1',
      title: 'Continued Growth',
      description: 'Serving 50+ enterprise clients with 60+ partner network members. Recognized as trusted authority in human-centered AI transformation.',
      icon: 'ChartBarIcon',
    },
  ];

  return (
    <section className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-primary mb-6">
            Our Journey
          </h2>
          <p className="text-lg text-muted-foreground font-body leading-relaxed">
            From founding vision to industry leadership, our timeline reflects steady growth built on proven results and trusted partnerships.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="relative">
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-border"></div>

            {milestones.map((milestone, index) => (
              <div
                key={milestone.id}
                className={`relative mb-12 lg:mb-16 ${
                  index % 2 === 0 ? 'lg:pr-1/2' : 'lg:pl-1/2 lg:text-right'
                }`}
              >
                <div className="lg:w-1/2 lg:inline-block">
                  <div className="bg-card rounded-lg p-6 shadow-card hover:shadow-strong transition-all duration-300 border border-border">
                    <div className={`flex items-start space-x-4 ${index % 2 !== 0 ? 'lg:flex-row-reverse lg:space-x-reverse' : ''}`}>
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                          <Icon name={milestone.icon as any} size={24} className="text-white" variant="solid" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-bold font-headline text-secondary">
                            {milestone.year}
                          </span>
                          <span className="text-xs text-muted-foreground font-body">
                            {milestone.quarter}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold font-headline text-foreground mb-2">
                          {milestone.title}
                        </h3>
                        <p className="text-muted-foreground font-body text-sm leading-relaxed">
                          {milestone.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="hidden lg:block absolute left-1/2 top-6 transform -translate-x-1/2 w-4 h-4 rounded-full bg-secondary border-4 border-background"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;